import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import useIsMobile from "../hooks/useIsMobile";
import Logo from "../Image/Logo.jpg";

interface SignupError {
  message: string;
  field?: string;
}

export default function Signup() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [error, setError] = useState<SignupError | null>(null);
  
  // Animation states - same as login
  const [showSplash, setShowSplash] = useState(true);
  const [logoVisible, setLogoVisible] = useState(false);
  const [logoSlideLeft, setLogoSlideLeft] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [loginContainerVisible, setLoginContainerVisible] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  // Below lg there is no left brand panel, so the splash logo must not slide toward it
  const isMobile = useIsMobile();

  useEffect(() => {
    // Check if user came from forgot password page or if it's a refresh
    const fromForgotPassword = location.state?.from === 'forgot-password';
    const hasShownSplash = sessionStorage.getItem('splashShown');
    
    if (fromForgotPassword) {
      // Skip splash animation if coming from forgot password
      setShowSplash(false);
      setAnimationComplete(true);
      setLoginContainerVisible(true);
      setLogoVisible(true);
      setLogoSlideLeft(true);
    } else if (hasShownSplash && !fromForgotPassword) {
      // Show splash on refresh or direct access
      sessionStorage.removeItem('splashShown');
      const sequence = async () => {
        setTimeout(() => setLogoVisible(true), 400);
        setTimeout(() => setLogoSlideLeft(true), 2000);
        setTimeout(() => setAnimationComplete(true), 2600);
        setTimeout(() => setShowSplash(false), 2900);
        setTimeout(() => setLoginContainerVisible(true), 3100);
        setTimeout(() => sessionStorage.setItem('splashShown', 'true'), 3200);
      };
      sequence();
    } else {
      // Normal startup sequence
      const sequence = async () => {
        setTimeout(() => setLogoVisible(true), 400);
        setTimeout(() => setLogoSlideLeft(true), 2000);
        setTimeout(() => setAnimationComplete(true), 2600);
        setTimeout(() => setShowSplash(false), 2900);
        setTimeout(() => setLoginContainerVisible(true), 3100);
        setTimeout(() => sessionStorage.setItem('splashShown', 'true'), 3200);
      };
      sequence();
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (password !== confirmPassword) {
      setError({
        message: 'Passwords do not match',
        field: 'confirmPassword'
      });
      return;
    }

    if (password.length < 8) {
      setError({
        message: 'Password must be at least 8 characters long',
        field: 'password'
      });
      return;
    }

    if (!agreeTerms) {
      setError({
        message: 'Please agree to the Terms of Service and Privacy Policy',
        field: 'terms'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // TODO: Implement signup API call
      console.log('Signup attempt:', { firstName, lastName, email, password });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Redirect to login after successful signup
      navigate("/login", { 
        state: { 
          message: "Account created successfully! Please sign in.",
          from: 'signup' 
        }
      });
    } catch (err: any) {
      console.error('Signup error:', err);
      setError({
        message: err.message || 'Failed to create account. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--bg-main)' }}>
      {/* Splash Screen */}
      {showSplash && (
        <div className={`
          fixed inset-0 z-50 login-layout-container
          transition-all duration-500 ease-in-out
          ${logoSlideLeft ? '' : ''}
        `} style={{ 
          background: 'linear-gradient(145deg, #111827 0%, #0f2036 48%, #1e3a5f 100%)',
          opacity: showSplash ? 1 : 0,
          visibility: showSplash ? 'visible' : 'hidden',
          transition: 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: showSplash ? 100 : -1
        }}>
          <div className="absolute inset-0 surface-grid opacity-30" />
          {/* Centered Logo Container that moves to final position */}
          <div className="splash-logo-container splash-slide-transition absolute" style={{
            willChange: 'transform, opacity',
            left: logoSlideLeft && !isMobile ? '25%' : '50%',
            top: '50%',
            transform: logoSlideLeft
              ? `translate(-50%, -50%) scale(${isMobile ? 0.85 : 1})`
              : logoVisible
                ? 'translate(-50%, -50%) scale(1)'
                : 'translate(-50%, -50%) scale(0.9)',
            transition: 'all 1000ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            zIndex: 60
          }}>
            <div className="w-36 h-36 sm:w-48 sm:h-48 lg:w-56 lg:h-56 rounded-3xl p-3 lg:p-4 shadow-panel bg-white border border-white/40">
              <img 
                src={Logo} 
                alt="Logo" 
                className="w-full h-full rounded-2xl transition-all duration-700 ease-out object-contain"
                style={{ 
                  borderRadius: '16px',
                  transform: logoVisible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.95)',
                  transition: 'all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Final Signup Layout */}
      {animationComplete && (
        <div className="min-h-screen flex" style={{
          position: showSplash ? 'fixed' : 'static',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: animationComplete ? 1 : 0,
          transform: animationComplete ? 'scale(1)' : 'scale(0.98)',
          transition: 'all 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          zIndex: showSplash ? 90 : 1
        }}>
          {/* Left Section - Brand */}
          <div className="hidden lg:flex lg:w-[42%] relative overflow-hidden bg-brand-900 text-white">
            <div className="absolute inset-0 surface-grid opacity-20" />
            <div className="relative z-10 flex flex-col justify-between w-full p-10 xl:p-12">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-md bg-white p-1.5">
                  <img src={Logo} alt="Logo" className="h-full w-full object-contain" />
                </div>
                <div>
                  <p className="text-sm font-extrabold">FleetWMS</p>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/50 font-bold">Create Access</p>
                </div>
              </div>
              <div>
                <h2 className="text-4xl font-extrabold mb-4 leading-tight">Join the<br />operations floor</h2>
                <p className="text-white/65 text-base leading-relaxed max-w-md">
                  Create your account and start managing inbound, outbound, and inventory from one console.
                </p>
              </div>
              <p className="text-xs text-white/35">Role-based onboarding · Secure by default</p>
            </div>
          </div>

          {/* Right Section - Signup Form */}
          <div className="w-full lg:w-[58%] flex items-center justify-center p-4 sm:p-8 bg-ink-100" style={{ 
            opacity: loginContainerVisible ? 1 : 0,
            transform: loginContainerVisible ? 'translateY(0)' : 'translateY(100%)',
            transition: 'all 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: '0ms'
          }}>
            <div className="w-full max-w-md">
              <div className="bg-white rounded-lg shadow-panel p-6 sm:p-8 border border-ink-200" style={{ 
                opacity: loginContainerVisible ? 1 : 0,
                transform: loginContainerVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
                transition: 'all 700ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                transitionDelay: loginContainerVisible ? '200ms' : '0ms'
              }}>
                {/* Header */}
                <div className="mb-6">
                  <h2 className="text-2xl font-extrabold mb-1 text-ink-900">Create Account</h2>
                  <p className="text-sm text-ink-500">Sign up to get started</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in">
                    <div className="flex items-center text-red-700">
                      <i className="fas fa-exclamation-circle mr-2"></i>
                      <span className="text-sm">{error.message}</span>
                    </div>
                  </div>
                )}

                {/* Signup Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                        First Name
                      </label>
                      <input
                        id="firstName"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
                        style={{ 
                          borderColor: 'var(--border-color)',
                          background: 'white',
                          color: 'var(--text-primary)'
                        }}
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                        Last Name
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
                        style={{ 
                          borderColor: 'var(--border-color)',
                          background: 'white',
                          color: 'var(--text-primary)'
                        }}
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
                      style={{ 
                        borderColor: error?.field === 'email' ? '#ef4444' : 'var(--border-color)',
                        background: error?.field === 'email' ? '#fef2f2' : 'white',
                        color: 'var(--text-primary)'
                      }}
                      placeholder="Enter your email"
                    />
                  </div>

                  {/* Password Field */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="w-full px-4 py-3 pr-12 border-2 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
                        style={{ 
                          borderColor: error?.field === 'password' ? '#ef4444' : 'var(--border-color)',
                          background: error?.field === 'password' ? '#fef2f2' : 'white',
                          color: 'var(--text-primary)'
                        }}
                        placeholder="Create a password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {showPassword ? (
                          <i className="fas fa-eye-slash"></i>
                        ) : (
                          <i className="fas fa-eye"></i>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="w-full px-4 py-3 pr-12 border-2 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-all duration-200"
                        style={{ 
                          borderColor: error?.field === 'confirmPassword' ? '#ef4444' : 'var(--border-color)',
                          background: error?.field === 'confirmPassword' ? '#fef2f2' : 'white',
                          color: 'var(--text-primary)'
                        }}
                        placeholder="Confirm your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        {showConfirmPassword ? (
                          <i className="fas fa-eye-slash"></i>
                        ) : (
                          <i className="fas fa-eye"></i>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Terms & Conditions Checkbox */}
                  <div className="flex items-start">
                    <input
                      id="agreeTerms"
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <label htmlFor="agreeTerms" className="ml-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      I agree to the{' '}
                      <button type="button" className="font-medium" style={{ color: '#1e3a5f' }}>
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button type="button" className="font-medium" style={{ color: '#1e3a5f' }}>
                        Privacy Policy
                      </button>
                    </label>
                  </div>

                  {/* Signup Button */}
                  <button
                    type="submit"
                    disabled={isLoading || !firstName || !lastName || !email || !password || !confirmPassword || !agreeTerms}
                    className="w-full text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed btn-modern"
                    style={{ 
                      background: isLoading || !firstName || !lastName || !email || !password || !confirmPassword || !agreeTerms ? '#94a3b8' : '#1e3a5f',
                    }}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-3"></i>
                        Creating account...
                      </div>
                    ) : (
                      <>
                        <i className="fas fa-user-plus mr-2"></i>
                        Create Account
                      </>
                    )}
                  </button>
                </form>

                {/* Login Link */}
                <div className="mt-6 text-center">
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Already have an account?{' '}
                    <Link 
                      to="/login" 
                      className="font-medium transition-colors hover:underline"
                      style={{ color: '#1e3a5f' }}
                      state={{ from: 'signup' }}
                    >
                      Sign in here
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
